import { z } from 'zod';
import { MaterialFormulaSchema } from './material-formula';

const SupplierSchema = z.object({
  id: z.number(),
  name: z.string(),
  price: z.number(),
  unit: z.string(),
});

const StyleSchema = z.object({
  id: z.number(),
  name: z.string(),
  coverage: z.number().nullable().optional(),
  coverageUnit: z.string().nullable().optional(),
  suppliers: z.array(SupplierSchema),
});

const BrandSchema = z.object({
  id: z.string().nullable(),
  name: z.string(),
  styles: z.array(StyleSchema),
});

export const MaterialSchema = z.object({
  id: z.number(),
  name: z.string(),
  brands: z.array(BrandSchema),
  formulas: z.array(MaterialFormulaSchema).optional(),
});

export const MaterialsListIDXSchema = z.object({
  mliid: z.number().optional(),
  mlimlid: z.number().nullable().optional(),
  mlimid: z.number().nullable().optional(),
  mlimiid: z.number().nullable().optional(),
  mliordered: z.number().nullable().optional().default(0),
  mlipulled: z.number().nullable().optional().default(0),
  mlireceived: z.number().nullable().optional(),
  mlicost: z.number().nullable().optional().default(0),
  mlireturned: z.number().nullable().optional(),
  mliopt: z.string().nullable().optional(),
});

export const MaterialsListSchema = z.object({
  mlid: z.number().optional(),
  mlcoid: z.number().nullable().optional(),
  mljid: z.number().nullable().optional(),
  mlwoid: z.number().nullable().optional(),
  mlwo: z.string().nullable().optional(),
  mlsid: z.number().nullable().optional(),
  mlsgid: z.number().nullable().optional(),
  mlitemorder: z.number().nullable().optional(),
  mlstatus: z.string().nullable().optional(),
  mlstatusreason: z.string().nullable().optional(),
  mlordertype: z.string().nullable().optional(),
  mlcreator: z.string().nullable().optional(),
  mlcreatorid: z.number().nullable().optional(),
  mlcreated: z.string().nullable().optional(), // Date as ISO string for frontend
  mlorder: z.string().nullable().optional(),
  mlorderid: z.number().nullable().optional(),
  mlordered: z.string().nullable().optional(), // Date as ISO string
  mlcancel: z.string().nullable().optional(),
  mlcancelid: z.number().nullable().optional(),
  mlcancelled: z.string().nullable().optional(), // Date as ISO string
  mldroploc: z.string().nullable().optional(),
  mldropwhid: z.number().nullable().optional(),
  mldropeta: z.string().nullable().optional(), // Date as ISO string
  mlreceive: z.string().nullable().optional(),
  mlreceived: z.string().nullable().optional(), // Date as ISO string
  mlproduction: z.string().nullable().optional(), // Date as ISO string
  mlrstatus: z.string().nullable().optional(),
  mlrnotes: z.string().nullable().optional(),
  mlrproblem: z.string().nullable().optional(),
  mlcalltosched: z.string().nullable().optional(),
  mlrpic: z.number().nullable().optional(),
  mlassign: z.string().nullable().optional(),
  mlassigndts: z.string().nullable().optional(), // Date as ISO string
  mlassignid: z.number().nullable().optional(),
  mlassignby: z.string().nullable().optional(),
  mlassignbyid: z.number().nullable().optional(),
  mlfix: z.string().nullable().optional(),
  mlfixid: z.number().nullable().optional(),
  mlfixed: z.string().nullable().optional(), // Date as ISO string
  mlfixnotes: z.string().nullable().optional(),
  mlnotes: z.string().nullable().optional(),
  mlintnotes: z.string().nullable().optional(),
  mlorderdelivfee: z.number().nullable().optional().default(0),
  mlordertot: z.number().nullable().optional().default(0),
  mlcredit: z.number().nullable().optional().default(0),
  mlkey: z.string().nullable().optional(),
  mlacl: z.string().nullable().optional(),
  mlseen: z.string().nullable().optional(), // Date as ISO string
  mlseencancel: z.string().nullable().optional(), // Date as ISO string
  mlid2: z.string().nullable().optional(),
  materialsListIDX: z.array(MaterialsListIDXSchema).optional(),
});

export type Material = z.infer<typeof MaterialSchema>;
export type Brand = z.infer<typeof BrandSchema>;
export type Style = z.infer<typeof StyleSchema>;
export type Supplier = z.infer<typeof SupplierSchema>;
export type MaterialsList = z.infer<typeof MaterialsListSchema>;
export type MaterialsListIDX = z.infer<typeof MaterialsListIDXSchema>;